import { useState, useEffect, useRef } from "react";
import {
  X,
  CheckCircle2,
  Loader2,
  Trash2,
  Upload,
  ImageOff,
  Search,
  ChevronDown,
  Plus,
} from "lucide-react";
import { adminAPI } from "../utils/adminApi";
import type { TopicItem } from "../utils/adminTypes";
import { QuestionType } from "../utils/adminTypes";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

type Lang = "oz" | "uz" | "ru";

const LANGS: { key: Lang; label: string }[] = [
  { key: "oz", label: "O'zbekcha" },
  { key: "uz", label: "Ўзбекча" },
  { key: "ru", label: "Русский" },
];

const QUESTION_TYPES: { key: QuestionType; label: string }[] = [
  { key: QuestionType.ODDIY, label: "Oddiy" },
  { key: QuestionType.SONLI, label: "Sonli" },
  { key: QuestionType.KALIT_SOZLI, label: "Kalit so'zli" },
  { key: QuestionType.MURAKKAB, label: "Murakkab" },
];

interface EditQuestionModalProps {
  questionId: number | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

interface ChoiceForm {
  id: number;
  order_index: number;
  text_oz: string;
  text_uz: string;
  text_ru: string;
}

interface QuestionForm {
  question_oz: string;
  question_uz: string;
  question_ru: string;
  explanation_oz: string;
  explanation_uz: string;
  explanation_ru: string;
  correct_answer_index: number;
  photo_url: string | null;
  audio_oz_url: string | null;
  audio_uz_url: string | null;
  audio_ru_url: string | null;
  ticketId: number | null;
  topicId: number | null;
  questionType: QuestionType;
  keywords: string[];
  choices: ChoiceForm[];
}

const AUDIO_ACCEPT = ".ogg,.mp3,.m4a,.wav";
const AUDIO_MAX_SIZE = 10 * 1024 * 1024;
const audioField = (lang: Lang) => `audio_${lang}_url` as const;

// oz (lotin) va uz (kirill) — bitta til, bitta audio. Faqat ru boshqacha.
const AUDIO_LANGS: { key: Lang; label: string }[] = [
  { key: "uz", label: "O'zbek" },
  { key: "ru", label: "Rus" },
];

export default function EditQuestionModal({
  questionId,
  open,
  onClose,
  onSaved,
  onError,
  onSuccess,
}: EditQuestionModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [activeLang, setActiveLang] = useState<Lang>("oz");
  const [form, setForm] = useState<QuestionForm | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioFileInputRef = useRef<HTMLInputElement>(null);
  const [audioUploadLang, setAudioUploadLang] = useState<Lang | null>(null);
  const [uploadingAudioLang, setUploadingAudioLang] = useState<Lang | null>(
    null,
  );
  const [playingLang, setPlayingLang] = useState<Lang | null>(null);
  // Topic selector state'lari
  const [topics, setTopics] = useState<TopicItem[]>([]);
  const [isTopicOpen, setIsTopicOpen] = useState(false);
  const [topicSearch, setTopicSearch] = useState("");
  const [photoCacheBust, setPhotoCacheBust] = useState(0);
  const [keywordInput, setKeywordInput] = useState("");
  // Topiclarni yuklash (modal ochilganda)
  useEffect(() => {
    if (!open) return;
    adminAPI
      .getTopicsForSelect("")
      .then((res: any) => {
        setTopics(res.data || res);
      })
      .catch((err) => console.error("Mavzular yuklanmadi:", err));
  }, [open]);
  // ─── Modal ochilganda — getOne ─────────────────────────────────
  useEffect(() => {
    if (!open || !questionId) {
      setForm(null);
      setActiveLang("oz");
      setKeywordInput("");
      setPlayingLang(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await adminAPI.getQuestion(questionId);
        if (cancelled) return;

        const data = res.data;
        setForm({
          question_oz: data.question_oz || "",
          question_uz: data.question_uz || "",
          question_ru: data.question_ru || "",
          explanation_oz: data.explanation_oz || "",
          explanation_uz: data.explanation_uz || "",
          explanation_ru: data.explanation_ru || "",
          correct_answer_index: data.correct_answer_index,
          photo_url: data.photo_url || null,
          audio_oz_url: data.audio_oz_url || null,
          audio_uz_url: data.audio_uz_url || null,
          audio_ru_url: data.audio_ru_url || null,
          ticketId: data.ticketId ?? null,
          topicId: data.topicId ?? null,
          questionType: data.question_type ?? QuestionType.ODDIY,
          keywords: data.keywords ?? [],
          choices: data.choices
            .slice()
            .sort((a, b) => a.order_index - b.order_index)
            .map((c) => ({
              id: c.id,
              order_index: c.order_index,
              text_oz: c.text_oz || "",
              text_uz: c.text_uz || "",
              text_ru: c.text_ru || "",
            })),
        });
      } catch {
        onError("Savol ma'lumotlari yuklanmadi");
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, questionId, onClose, onError]);

  // ─── ESC bilan yopish ─────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  if (!open) return null;
  // Topic selector helpers (form yuklangach hisoblanadi)
  const selectedTopic = form ? topics.find((t) => t.id === form.topicId) : null;
  const filteredTopics = topics.filter((t) =>
    `${t.name_oz}`.toLowerCase().includes(topicSearch.toLowerCase()),
  );

  // ─── Validation ────────────────────────────────────────────────
  const isValid = (): boolean => {
    if (!form) return false;
    if (!form.topicId) return false; // ← YANGI
    if (!form.question_oz.trim()) return false;
    if (form.choices.some((c) => !c.text_oz.trim())) return false;
    if (!form.correct_answer_index) return false;
    return true;
  };
  // ─── Photo upload ──────────────────────────────────────────────
  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !form) return;

    // Validation
    if (!file.type.startsWith("image/")) {
      onError("Faqat rasm fayli yuklash mumkin");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onError("Rasm hajmi 5MB dan oshmasligi kerak");
      return;
    }

    if (!questionId) {
      onError("Savol ID topilmadi");
      return;
    }

    setUploadingPhoto(true);
    try {
      const res = await adminAPI.uploadQuestionPhoto(questionId, file);
      setForm({ ...form, photo_url: res.data.photo_url });
      setPhotoCacheBust(Date.now());
      onSuccess("Rasm yuklandi");
    } catch {
      onError("Rasm yuklashda xato");
    } finally {
      setUploadingPhoto(false);
      // Input reset — bir xil fayl qayta tanlanishi mumkin bo'lsin
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePhoto = () => {
    if (!form) return;
    setForm({ ...form, photo_url: null });
  };

  // ─── Audio upload ──────────────────────────────────────────────
  const openAudioPicker = (lang: Lang) => {
    setAudioUploadLang(lang);
    audioFileInputRef.current?.click();
  };

  const handleAudioSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const lang = audioUploadLang;
    if (!file || !form || !lang) return;

    const allowedExt = [".ogg", ".mp3", ".m4a", ".wav"];
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowedExt.includes(ext)) {
      onError("Faqat audio fayli qabul qilinadi (.ogg, .mp3, .m4a, .wav)");
      if (audioFileInputRef.current) audioFileInputRef.current.value = "";
      return;
    }
    if (file.size > AUDIO_MAX_SIZE) {
      onError("Audio hajmi 10MB dan oshmasligi kerak");
      if (audioFileInputRef.current) audioFileInputRef.current.value = "";
      return;
    }

    if (!questionId) {
      onError("Savol ID topilmadi");
      return;
    }

    setUploadingAudioLang(lang);
    try {
      const res = await adminAPI.uploadQuestionAudio(questionId, lang, file);
      setForm({ ...form, [audioField(lang)]: res.data.audio_url });
      onSuccess("Audio yuklandi");
    } catch (e: any) {
      onError(e?.response?.data?.message || "Audio yuklashda xato");
    } finally {
      setUploadingAudioLang(null);
      setAudioUploadLang(null);
      if (audioFileInputRef.current) audioFileInputRef.current.value = "";
    }
  };

  // ─── Kalit so'zlar qo'shish/o'chirish ──────────────────────────
  const addKeyword = () => {
    if (!form) return;
    const value = keywordInput.trim();
    if (!value || form.keywords.includes(value)) {
      setKeywordInput("");
      return;
    }
    setForm({ ...form, keywords: [...form.keywords, value] });
    setKeywordInput("");
  };

  const removeKeyword = (idx: number) => {
    if (!form) return;
    setForm({
      ...form,
      keywords: form.keywords.filter((_, i) => i !== idx),
    });
  };

  // ─── Save ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form || !questionId || !isValid()) return;
    setSaving(true);

    // 1. Faqat API chaqiruvini try/catch ichida ushlaymiz
    let saveOk = false;
    try {
      await adminAPI.updateQuestion(questionId, {
        question_oz: form.question_oz.trim(),
        question_uz: form.question_uz.trim(),
        question_ru: form.question_ru.trim(),
        explanation_oz: form.explanation_oz.trim(),
        explanation_uz: form.explanation_uz.trim(),
        explanation_ru: form.explanation_ru.trim(),
        correct_answer_index: form.correct_answer_index,
        photo_url: form.photo_url,
        ticketId: form.ticketId,
        topicId: form.topicId,
        question_type: form.questionType,
        keywords: form.keywords,
        choices: form.choices.map((c) => ({
          order_index: c.order_index,
          text_oz: c.text_oz.trim(),
          text_uz: c.text_uz.trim(),
          text_ru: c.text_ru.trim(),
        })),
      } as any);
      saveOk = true;
    } catch (e: any) {
      console.error("SAVE API ERROR:", e?.response?.status, e?.response?.data);
      onError(e?.response?.data?.message || "Saqlashda xato yuz berdi");
    } finally {
      setSaving(false);
    }

    if (saveOk) {
      onSuccess("Savol muvaffaqiyatli yangilandi");
      onSaved();
      onClose();
    }
  };

  // ─── Helpers ───────────────────────────────────────────────────
  const updateChoiceText = (orderIndex: number, lang: Lang, text: string) => {
    if (!form) return;
    const field = `text_${lang}` as const;
    setForm({
      ...form,

      choices: form.choices.map((c) =>
        c.order_index === orderIndex ? { ...c, [field]: text } : c,
      ),
    });
  };

  const photoFullUrl =
    form?.photo_url ?
      `${IMG_BASE}/${form.photo_url}${photoCacheBust ? `?v=${photoCacheBust}` : ""}`
    : null;

  const questionField = `question_${activeLang}` as keyof QuestionForm;
  const explanationField = `explanation_${activeLang}` as keyof QuestionForm;
  const choiceField = `text_${activeLang}` as const;

  // ─── Styles ────────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    background: "#0D1526",
    border: "1px solid #1E2D45",
    borderRadius: 10,
    color: "#E2E8F0",
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.75)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
      }}
      onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827",
          border: "1px solid #1E2D45",
          borderRadius: 20,
          width: "100%",
          maxWidth: 600,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
        {/* ─── HEADER ───────────────────────────────────────── */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #1E2D45",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <div>
            <h3
              style={{
                color: "#fff",
                fontWeight: 800,
                fontSize: 15,
                margin: 0,
              }}>
              Savolni tahrirlash
            </h3>
            {questionId && (
              <p
                style={{
                  color: "#475569",
                  fontSize: 11,
                  margin: "2px 0 0",
                  fontWeight: 600,
                }}>
                #{questionId}
                {form?.ticketId ? ` · Bilet ${form.ticketId}` : ""}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: "transparent",
              border: "none",
              color: "#64748B",
              cursor: saving ? "not-allowed" : "pointer",
              padding: 4,
              display: "flex",
            }}>
            <X size={20} />
          </button>
        </div>

        {/* ─── LANG TABS ────────────────────────────────────── */}
        {form && !loading && (
          <div
            style={{
              padding: "12px 24px",
              borderBottom: "1px solid #1E2D45",
              display: "flex",
              gap: 6,
            }}>
            {LANGS.map((l) => (
              <button
                key={l.key}
                onClick={() => setActiveLang(l.key)}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  border: `1px solid ${
                    activeLang === l.key ? "#3B82F6" : "#1E2D45"
                  }`,
                  background:
                    activeLang === l.key ?
                      "rgba(59,130,246,.12)"
                    : "transparent",
                  color: activeLang === l.key ? "#3B82F6" : "#64748B",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}>
                {l.label}
              </button>
            ))}
          </div>
        )}

        {/* ─── BODY ─────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {loading ?
            <div
              style={{
                padding: 60,
                textAlign: "center",
                color: "#475569",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
              }}>
              <Loader2
                size={28}
                style={{
                  color: "#3B82F6",
                  animation: "spin 1s linear infinite",
                }}
              />
              <span style={{ fontSize: 13 }}>Yuklanmoqda...</span>
            </div>
          : form ?
            <>
              {/* ─── PHOTO ─── */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Rasm</label>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  style={{ display: "none" }}
                />

                {photoFullUrl ?
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        width: "100%",
                        height: 200,
                        borderRadius: 12,
                        overflow: "hidden",
                        background: "#000",
                        border: "1px solid #1E2D45",
                      }}>
                      <img
                        src={photoFullUrl}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                    {/* Overlay button — faqat almashtirish */}
                    <div style={{ position: "absolute", top: 8, right: 8 }}>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        title="Rasmni almashtirish"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "8px 12px",
                          borderRadius: 8,
                          border: "none",
                          background: "rgba(59,130,246,.95)",
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: uploadingPhoto ? "not-allowed" : "pointer",
                        }}>
                        {uploadingPhoto ?
                          <Loader2
                            size={14}
                            style={{ animation: "spin 1s linear infinite" }}
                          />
                        : <Upload size={14} />}
                        {uploadingPhoto ? "Yuklanmoqda..." : "Almashtirish"}
                      </button>
                    </div>
                  </div>
                : /* Rasm yo'q — upload zona */
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    style={{
                      width: "100%",
                      height: 140,
                      borderRadius: 12,
                      border: "2px dashed #1E2D45",
                      background: "#0D1526",
                      color: "#64748B",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      cursor: uploadingPhoto ? "not-allowed" : "pointer",
                    }}>
                    {uploadingPhoto ?
                      <>
                        <Loader2
                          size={24}
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                        <span style={{ fontSize: 12 }}>Yuklanmoqda...</span>
                      </>
                    : <>
                        <ImageOff size={24} />
                        <span style={{ fontSize: 12, fontWeight: 600 }}>
                          Rasm yo'q — yuklash uchun bosing
                        </span>
                      </>
                    }
                  </button>
                }
              </div>
              <div style={{ marginBottom: 20, position: "relative" }}>
                <label style={labelStyle}>Mavzu *</label>

                {/* Trigger */}
                <div
                  onClick={() => setIsTopicOpen(!isTopicOpen)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    background: "#0D1526",
                    border: `1px solid ${isTopicOpen ? "#3B82F6" : "#1E2D45"}`,
                    borderRadius: 10,
                    color: selectedTopic ? "#E2E8F0" : "#64748B",
                    fontSize: 13,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "border-color 0.15s",
                  }}>
                  <span
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                    {selectedTopic ?
                      selectedTopic.name_oz || selectedTopic.name_uz || "—"
                    : "Mavzu tanlang..."}
                  </span>
                  {selectedTopic && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setForm({ ...form, topicId: null });
                      }}
                      title="Tozalash"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 2,
                        cursor: "pointer",
                        color: "#64748B",
                        display: "flex",
                      }}>
                      <X size={14} />
                    </button>
                  )}
                  <ChevronDown
                    size={14}
                    style={{
                      color: "#64748B",
                      transform: isTopicOpen ? "rotate(180deg)" : "rotate(0)",
                      transition: "transform 0.15s",
                    }}
                  />
                </div>

                {/* Dropdown */}
                {isTopicOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      right: 0,
                      background: "#111827",
                      border: "1px solid #1E2D45",
                      borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(0,0,0,.4)",
                      zIndex: 100,
                      maxHeight: 260,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}>
                    {/* Search */}
                    <div
                      style={{
                        padding: 8,
                        borderBottom: "1px solid #1E2D45",
                        position: "relative",
                      }}>
                      <Search
                        size={14}
                        style={{
                          position: "absolute",
                          left: 18,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#475569",
                        }}
                      />
                      <input
                        autoFocus
                        value={topicSearch}
                        onChange={(e) => setTopicSearch(e.target.value)}
                        placeholder="Mavzu qidirish..."
                        style={{
                          width: "100%",
                          paddingLeft: 32,
                          paddingRight: 12,
                          paddingTop: 8,
                          paddingBottom: 8,
                          background: "#0D1526",
                          border: "1px solid #1E2D45",
                          borderRadius: 8,
                          color: "#E2E8F0",
                          fontSize: 12,
                          outline: "none",
                          fontFamily: "inherit",
                        }}
                      />
                    </div>

                    {/* List */}
                    <div style={{ overflowY: "auto", flex: 1 }}>
                      {filteredTopics.length === 0 ?
                        <div
                          style={{
                            padding: 20,
                            textAlign: "center",
                            color: "#475569",
                            fontSize: 12,
                          }}>
                          Mavzu topilmadi
                        </div>
                      : filteredTopics.map((topic) => {
                          const isSelected = topic.id === form.topicId;
                          const name = topic.name_oz || topic.name_uz || "—";
                          return (
                            <div
                              key={topic.id}
                              onClick={() => {
                                setForm({ ...form, topicId: topic.id });
                                setIsTopicOpen(false);
                                setTopicSearch("");
                              }}
                              style={{
                                padding: "10px 14px",
                                cursor: "pointer",
                                background:
                                  isSelected ?
                                    "rgba(59,130,246,.12)"
                                  : "transparent",
                                color: isSelected ? "#3B82F6" : "#E2E8F0",
                                fontSize: 13,
                                fontWeight: isSelected ? 700 : 500,
                                borderBottom: "1px solid rgba(255,255,255,.03)",
                              }}>
                              {name}
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                )}
              </div>
              {/* ─── QUESTION TEXT ─── */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>
                  Savol matni · {LANGS.find((l) => l.key === activeLang)?.label}
                  {activeLang === "oz" && " *"}
                </label>
                <textarea
                  value={form[questionField] as string}
                  onChange={(e) =>
                    setForm({ ...form, [questionField]: e.target.value })
                  }
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
                  placeholder="Savol matnini kiriting..."
                />
              </div>

              {/* ─── EXPLANATION ─── */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>
                  Izoh ({LANGS.find((l) => l.key === activeLang)?.label})
                </label>
                <textarea
                  value={form[explanationField] as string}
                  onChange={(e) =>
                    setForm({ ...form, [explanationField]: e.target.value })
                  }
                  rows={2}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 60 }}
                  placeholder="Izoh (ixtiyoriy)..."
                />
              </div>

              {/* ─── SAVOL TURI ─── */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Savol turi</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {QUESTION_TYPES.map((qt) => {
                    const active = form.questionType === qt.key;
                    return (
                      <button
                        key={qt.key}
                        onClick={() =>
                          setForm({ ...form, questionType: qt.key })
                        }
                        style={{
                          padding: "6px 14px",
                          borderRadius: 8,
                          border: `1px solid ${active ? "#3B82F6" : "#1E2D45"}`,
                          background:
                            active ? "rgba(59,130,246,.12)" : "transparent",
                          color: active ? "#3B82F6" : "#64748B",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}>
                        {qt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ─── KALIT SO'ZLAR ─── */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Kalit so'zlar</label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginBottom: form.keywords.length > 0 ? 8 : 0,
                  }}>
                  {form.keywords.map((kw, idx) => (
                    <span
                      key={`${kw}_${idx}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "5px 10px",
                        borderRadius: 8,
                        background: "rgba(59,130,246,.1)",
                        border: "1px solid rgba(59,130,246,.25)",
                        color: "#3B82F6",
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                      {kw}
                      <button
                        onClick={() => removeKeyword(idx)}
                        title="O'chirish"
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#3B82F6",
                          cursor: "pointer",
                          display: "flex",
                          padding: 0,
                        }}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addKeyword();
                      }
                    }}
                    placeholder="Kalit so'z kiriting..."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    onClick={addKeyword}
                    disabled={!keywordInput.trim()}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "0 14px",
                      borderRadius: 10,
                      border: "1px solid rgba(59,130,246,.3)",
                      background:
                        !keywordInput.trim() ? "transparent" : (
                          "rgba(59,130,246,.1)"
                        ),
                      color: !keywordInput.trim() ? "#334155" : "#3B82F6",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: !keywordInput.trim() ? "not-allowed" : "pointer",
                    }}>
                    <Plus size={13} /> Qo'shish
                  </button>
                </div>
              </div>

              {/* ─── AUDIO ─── */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Audio izoh</label>

                <input
                  ref={audioFileInputRef}
                  type="file"
                  accept={AUDIO_ACCEPT}
                  onChange={handleAudioSelect}
                  style={{ display: "none" }}
                />

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {AUDIO_LANGS.map((l) => {
                    const url = form[audioField(l.key)];
                    const fullUrl = url ? `${IMG_BASE}/${url}` : null;
                    const isUploading = uploadingAudioLang === l.key;
                    const isPlaying = playingLang === l.key;
                    return (
                      <div
                        key={l.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: 10,
                          borderRadius: 10,
                          background: "#0D1526",
                          border: "1px solid #1E2D45",
                        }}>
                        <span
                          style={{
                            color: "#94A3B8",
                            fontSize: 12,
                            fontWeight: 700,
                            width: 70,
                            flexShrink: 0,
                          }}>
                          {l.label}
                        </span>

                        <span
                          style={{
                            flex: 1,
                            color: fullUrl ? "#E2E8F0" : "#475569",
                            fontSize: 12,
                            fontStyle: fullUrl ? "normal" : "italic",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {fullUrl ? url : "yuklanmagan"}
                        </span>

                        {fullUrl && (
                          <button
                            onClick={() =>
                              setPlayingLang(isPlaying ? null : l.key)
                            }
                            title="Tinglash"
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              border: "1px solid rgba(34,197,94,.3)",
                              background: "rgba(34,197,94,.08)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              flexShrink: 0,
                              color: "#22C55E",
                              fontSize: 12,
                            }}>
                            {isPlaying ? "■" : "▶"}
                          </button>
                        )}

                        <button
                          onClick={() => openAudioPicker(l.key)}
                          disabled={isUploading}
                          title={fullUrl ? "Almashtirish" : "Yuklash"}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 10px",
                            borderRadius: 8,
                            border: "1px solid rgba(59,130,246,.3)",
                            background:
                              isUploading ? "transparent" : (
                                "rgba(59,130,246,.1)"
                              ),
                            color: isUploading ? "#334155" : "#3B82F6",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: isUploading ? "not-allowed" : "pointer",
                            flexShrink: 0,
                          }}>
                          {isUploading ?
                            <Loader2
                              size={12}
                              style={{ animation: "spin 1s linear infinite" }}
                            />
                          : <Upload size={12} />}
                          {fullUrl ? "Almashtirish" : "Yuklash"}
                        </button>

                        {isPlaying && fullUrl && (
                          <audio
                            src={fullUrl}
                            autoPlay
                            style={{ display: "none" }}
                            onEnded={() => setPlayingLang(null)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p
                  style={{
                    color: "#475569",
                    fontSize: 11,
                    marginTop: 8,
                    fontStyle: "italic",
                  }}>
                  Formatlar: .ogg, .mp3, .m4a, .wav · Maks. 10MB
                </p>
              </div>

              {/* ─── CHOICES ─── */}
              <div>
                <label style={labelStyle}>
                  Variantlar (to'g'ri javobni tanlang)
                </label>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {form.choices.map((choice) => {
                    const isCorrect =
                      choice.order_index === form.correct_answer_index;
                    return (
                      <div
                        key={choice.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: 10,
                          borderRadius: 10,
                          background:
                            isCorrect ? "rgba(34,197,94,.08)" : "#0D1526",
                          border:
                            isCorrect ?
                              "1px solid rgba(34,197,94,.3)"
                            : "1px solid #1E2D45",
                        }}>
                        <button
                          onClick={() =>
                            setForm({
                              ...form,
                              correct_answer_index: choice.order_index,
                            })
                          }
                          title="To'g'ri javob deb belgilash"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: isCorrect ? "#22C55E" : "#1E2D45",
                            color: isCorrect ? "#fff" : "#64748B",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 800,
                            border: "none",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}>
                          {choice.order_index}
                        </button>

                        <input
                          value={choice[choiceField]}
                          onChange={(e) =>
                            updateChoiceText(
                              choice.order_index,
                              activeLang,
                              e.target.value,
                            )
                          }
                          placeholder={`Variant ${choice.order_index}`}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            background: "transparent",
                            border: "none",
                            color: "#E2E8F0",
                            fontSize: 13,
                            fontFamily: "inherit",
                            outline: "none",
                          }}
                        />

                        {isCorrect && (
                          <CheckCircle2
                            size={18}
                            style={{ color: "#22C55E", flexShrink: 0 }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p
                  style={{
                    color: "#475569",
                    fontSize: 11,
                    marginTop: 8,
                    fontStyle: "italic",
                  }}>
                  To'g'ri javob barcha tillar uchun bir xil
                </p>
              </div>
            </>
          : null}
        </div>

        {/* ─── FOOTER ──────────────────────────────────────── */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid #1E2D45",
            display: "flex",
            gap: 10,
            background: "#0D1526",
          }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: 12,
              border: "1px solid #1E2D45",
              background: "transparent",
              color: "#E2E8F0",
              fontWeight: 700,
              fontSize: 13,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.5 : 1,
            }}>
            Bekor
          </button>
          <button
            onClick={handleSave}
            disabled={
              saving ||
              loading ||
              !isValid() ||
              uploadingPhoto ||
              !!uploadingAudioLang
            }
            style={{
              flex: 2,
              padding: "12px",
              borderRadius: 12,
              border: "none",
              background:
                isValid() && !saving && !uploadingPhoto && !uploadingAudioLang
                  ? "#3B82F6"
                : "#1E2D45",
              color:
                isValid() && !saving && !uploadingPhoto && !uploadingAudioLang
                  ? "#fff"
                : "#64748B",
              fontWeight: 700,
              fontSize: 13,
              cursor:
                saving || !isValid() || uploadingPhoto || uploadingAudioLang ?
                  "not-allowed"
                : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}>
            {saving ?
              <>
                <Loader2
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Saqlanmoqda...
              </>
            : "Saqlash"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
